import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLoginAuth } from "../hooks/useLoginAuth";
import { LoginFormFields, LoginSentPanel } from "../components/login/LoginFormFields";

/** NBSP keeps `·` separated from `VISION` across the duplicate-spans seam (normal trailing space collapses there). */
const LOGIN_MARQUEE =
  "VISION · FOUNDATION · SYNTHESIS · BUILD · INTEGRATE · VISION · FOUNDATION · SYNTHESIS · BUILD · INTEGRATE · VISION · FOUNDATION · SYNTHESIS · BUILD · INTEGRATE ·\u00a0";

const LINE_FIELDS = {
  inputClassName: "login-lab-line-input",
  passwordInputClassName: "login-lab-line-input pr-12",
  passwordToggleBtnClassName:
    "absolute right-1 top-1/2 -translate-y-1/2 text-[var(--t-meta)] hover:text-[var(--t-text)]",
  primaryBtnClassName: "t-btn-primary",
  secondaryBtnClassName: "t-btn-secondary",
  googleFirst: false,
};

const INK_MS = 1100;
const INK_MS_REDUCED = 120;

function useCursorWash() {
  const targetRef = useRef({ x: 50, y: 28 });
  const currentRef = useRef({ x: 50, y: 28 });
  const frameRef = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 28 });
  const [track, setTrack] = useState(false);

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || reduce) return undefined;
    setTrack(true);

    const tick = () => {
      const t = targetRef.current;
      const c = currentRef.current;
      const next = {
        x: c.x + (t.x - c.x) * 0.055,
        y: c.y + (t.y - c.y) * 0.055,
      };
      currentRef.current = next;
      setPos(next);
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  const onPointerMove = (e) => {
    if (!track) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    targetRef.current = {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  };

  return { pos, onPointerMove };
}

export default function LoginPage() {
  const auth = useLoginAuth();
  const { pos, onPointerMove } = useCursorWash();
  const [inkLoading, setInkLoading] = useState(false);
  const inkLockRef = useRef(false);

  const passwordSignIn = useCallback(async () => {
    if (inkLockRef.current || inkLoading) return;
    const data = await auth.passwordSignIn({ commitSession: false });
    if (!data) return;

    inkLockRef.current = true;
    setInkLoading(true);
    const reduced =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    await new Promise((resolve) => {
      window.setTimeout(resolve, reduced ? INK_MS_REDUCED : INK_MS);
    });
    await auth.commitSession(data, { boot: false });
  }, [auth, inkLoading]);

  const complete2fa = useCallback(async () => {
    if (inkLockRef.current || inkLoading) return;
    const data = await auth.complete2fa({ commitSession: false });
    if (!data) return;

    inkLockRef.current = true;
    setInkLoading(true);
    const reduced =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    await new Promise((resolve) => {
      window.setTimeout(resolve, reduced ? INK_MS_REDUCED : INK_MS);
    });
    await auth.commitSession(data, { boot: false });
  }, [auth, inkLoading]);

  const formAuth = useMemo(
    () => ({
      ...auth,
      passwordSignIn,
      complete2fa,
      pwBusy: auth.pwBusy || inkLoading,
      twoFaBusy: auth.twoFaBusy || inkLoading,
      busy: auth.busy || inkLoading,
      googleBusy: auth.googleBusy || inkLoading,
    }),
    [auth, passwordSignIn, complete2fa, inkLoading],
  );

  return (
    <div
      className="login-sealed pointer-events-auto fixed inset-0 z-0 flex flex-col overflow-hidden px-5 pb-6 pt-10 md:px-12 md:pb-10 md:pt-14"
      data-testid="login-page"
      onPointerMove={onPointerMove}
      style={{
        "--wash-x": `${pos.x}%`,
        "--wash-y": `${pos.y}%`,
      }}
      aria-busy={inkLoading || undefined}
    >
      <div className="login-sealed__wash" aria-hidden />
      <div className="login-sealed__aura-soft" aria-hidden />
      <div className="login-sealed__aura" aria-hidden />

      <div className="relative mx-auto flex min-h-0 w-full max-w-[440px] flex-1 flex-col justify-center">
        <header className="login-lab-enter mb-8 text-center">
          <h1 className="font-heading text-[clamp(3rem,9vw,5rem)] leading-none tracking-[-0.045em] !font-extralight">
            TAĪSTU
          </h1>
          <p className="login-lab-enter-delay mx-auto mt-4 max-w-sm text-sm leading-relaxed text-[var(--t-meta)]">
            Clarity, continuity, and forward movement.
          </p>
          <div
            className={`login-sealed__rule mx-auto mt-6 ${inkLoading ? "login-sealed__rule--inking" : ""}`}
            aria-hidden
            data-testid="login-ink-rule"
          >
            <div className="login-sealed__rule-ink" />
          </div>
        </header>

        <div className={`login-lab-enter-delay-2 ${inkLoading ? "pointer-events-none opacity-60" : ""}`}>
          <div className="login-lab-hybrid__seal border border-[var(--t-border)] bg-[rgba(252,252,252,0.92)] p-7 md:p-9">
            {!auth.sent ? (
              <LoginFormFields auth={formAuth} testIdPrefix="login" {...LINE_FIELDS} />
            ) : (
              <LoginSentPanel auth={formAuth} testIdPrefix="login" />
            )}
          </div>
        </div>
      </div>

      <div className="relative mt-8 w-full shrink-0" data-testid="login-marquee">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-[var(--t-bg)] to-transparent md:w-14" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-[var(--t-bg)] to-transparent md:w-14" />
        <div className="overflow-hidden">
          <div className="login-sealed__marquee-track flex w-max motion-reduce:animate-none">
            {Array.from({ length: 7 }, (_, i) => (
              <span
                key={i}
                className="label-eyebrow shrink-0 whitespace-nowrap py-1"
                aria-hidden={i > 0 ? true : undefined}
              >
                {LOGIN_MARQUEE}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
